import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { strategyService } from './strategyService';
import { 
  PageHeader, 
  MetricCard, 
  SectionCard, 
  DataTable, 
  LoadingState, 
  ErrorState,
  StatusBadge 
} from '../../components/common';
import { theme } from '../../design/theme';
import { EnterpriseDashboardResponse, WalletResponse, UsageResponse } from '../../types/api';
import { PaymentsPanel } from '../pricing/components/PaymentsPanel';

export const EnterpriseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<EnterpriseDashboardResponse | null>(null);
  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardData, walletData, usageData] = await Promise.all([
          strategyService.getEnterpriseDashboard(),
          strategyService.getWalletBalance(),
          strategyService.getTokenUsage()
        ]);
        setDashboard(dashboardData);
        setWallet(walletData);
        setUsage(usageData);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch enterprise data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <LoadingState />;
  if (error || !dashboard || !wallet || !usage) return <ErrorState message={error || "Data unavailable"} />;

  const teamColumns = [
    { header: 'TEAM NAME', accessor: 'teamName' as const },
    { 
      header: 'USAGE', 
      accessor: 'usage' as const,
      render: (val: number) => val.toLocaleString()
    },
    { 
      header: 'BUDGET', 
      accessor: 'budget' as const,
      render: (val: number) => `₹${val.toLocaleString()}`
    },
    {
      header: 'UTILIZATION',
      accessor: 'usage' as const,
      render: (_: any, row: any) => {
        const percent = (row.usage / row.budget) * 100;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
            <div style={{ flex: 1, height: '8px', backgroundColor: theme.colors.neutral[200], borderRadius: '4px' }}>
              <div style={{ 
                width: `${Math.min(percent, 100)}%`, 
                height: '100%', 
                backgroundColor: percent > 90 ? theme.colors.critical.main : theme.colors.healthy.main,
                borderRadius: '4px'
              }} />
            </div>
            <span style={theme.typography.caption}>{percent.toFixed(1)}%</span>
          </div>
        );
      }
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageHeader 
          title="Enterprise Control Plane" 
          subtitle="Real-time monitoring of token consumption, budget utilization, and team performance."
        />
        <button 
          onClick={() => navigate('/enterprise/checkout')}
          style={{
            backgroundColor: theme.colors.brand.main,
            color: 'white',
            padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
            borderRadius: '6px',
            border: 'none',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: theme.shadows.md
          }}
        >
          Add Credits
        </button>
      </div>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: theme.spacing.lg,
        marginBottom: theme.spacing.xl
      }}>
        <MetricCard 
          label="WALLET BALANCE" 
          value={`₹${wallet.balance.toLocaleString()}`} 
          status={wallet.balance < 10000 ? 'warning' : 'healthy'}
        />
        <MetricCard 
          label="TOTAL CONSUMPTION" 
          value={usage.totalTokens.toLocaleString()} 
          status="info"
        />
        <MetricCard 
          label="BUDGET UTILIZATION" 
          value={`${dashboard.budgetUtilization}%`} 
          status={dashboard.budgetUtilization > 90 ? 'critical' : 'healthy'}
        />
        <MetricCard 
          label="ACTIVE USERS" 
          value={dashboard.activeUsers.toString()} 
          status="healthy"
        />
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '2fr 1fr', 
        gap: theme.spacing.lg 
      }}>
        <SectionCard title="Team Breakdown">
          <DataTable 
            columns={teamColumns} 
            data={dashboard.teamUsage} 
          />
        </SectionCard>
        
        <SectionCard title="Usage Trend">
          <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${theme.colors.neutral[300]}` }}>
            [Line Chart: Token Consumption]
          </div>
        </SectionCard>
      </div>

      <div style={{ marginTop: theme.spacing.xl }}>
        <PaymentsPanel />
      </div>
    </div>
  );
};
